/**
 * @tt/shell-3d — post-processing (bloom + vignette)
 *
 * Built ENTIRELY from three's bundled example modules — no new npm deps:
 *  - {@link EffectComposer} + {@link RenderPass} (scene → HDR target)
 *  - {@link UnrealBloomPass} (glow on emissive / >1 highlights)
 *  - a small custom vignette {@link ShaderPass} (corner darken)
 *  - {@link OutputPass} (ACES tone map + sRGB, applied ONCE here so the
 *    bloom is computed in linear HDR — no double tone mapping)
 *
 * Perf-gated: the composer chain only exists on the `high` tier. On `low`
 * the shell renders straight to the canvas (`enabled === false`) and the
 * caller falls back to a cheap DOM vignette. `setTier()` rebuilds in place.
 */
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import type { QualityTier } from './quality';

/** A small, self-contained corner-vignette shader (linear space). */
const VignetteShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    // Lowered from 0.9: at 0.9 the corners crushed to rgb~(3,4,1) and read as
    // dead black instead of ground (the "letterbox" false-positive). 0.5 keeps
    // a clear atmospheric falloff while the scene stays legible to the edges.
    // Owner playtest (2026-06-29): at strength 0.5 / maxDarken 0.6 the corner
    // darkening crushed every map's edges into a muddy brown "fog" (it was the
    // vignette, not THREE.Fog). Pulled WAY down to a faint, far-out edge settle
    // so the built world reads crisp to the corners on all maps.
    uStrength: { value: 0.18 },
    uRadius: { value: 1.05 },
    uSoftness: { value: 0.45 },
    // A warm-dark TINT the corners multiply toward (not a replacement color),
    // so the ground keeps its hue at the edges instead of going pure black.
    uColor: { value: new THREE.Color(0x4a3a30) },
    // Hard floor: the vignette may darken a corner to at most (1 - uMaxDarken)
    // of its original luminance, so ground/scene NEVER fully crushes to black.
    uMaxDarken: { value: 0.25 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float uStrength;
    uniform float uRadius;
    uniform float uSoftness;
    uniform float uMaxDarken;
    uniform vec3 uColor;
    varying vec2 vUv;
    void main() {
      vec4 col = texture2D(tDiffuse, vUv);
      float dist = length(vUv - 0.5);
      float v = smoothstep(uRadius, uRadius - uSoftness, dist); // 1 center → 0 corner
      // amount of darken at this pixel, hard-capped so corners keep their scene.
      float amt = min((1.0 - v) * uStrength, uMaxDarken);
      col.rgb = mix(col.rgb, col.rgb * uColor, amt);
      gl_FragColor = col;
    }
  `,
};

export interface BloomOptions {
  /** Glow strength. Default 0.16 (was 0.32, 0.55 — both still bled a bright
   *  wash across the whole frame and turned candles/braziers/lanterns into
   *  white blobs that hid nearby geometry; 0.16 keeps a tasteful halo on light
   *  SOURCES without washing the ground/props). */
  strength?: number;
  /** Glow spread radius. Default 0.26 (was 0.32 — a tighter halo so the glow
   *  hugs the source instead of smearing across neighbouring rocks/cards). */
  radius?: number;
  /** Luminance above which pixels bloom (keeps the scene from washing
   *  out — only genuinely emissive/HDR highlights glow). Default 1.05 (was
   *  0.95, 0.82 — bright LIT toon surfaces reach ~0.9, so >1.0 restricts bloom
   *  to emissive flames/lava that are authored above unity). */
  threshold?: number;
}

export interface PostFXOptions {
  tier: QualityTier;
  bloom?: BloomOptions;
  vignette?: { strength?: number; radius?: number; softness?: number; color?: number; maxDarken?: number };
}

export interface PostFX {
  /** The composer (high tier only); null when rendering direct. */
  composer: EffectComposer | null;
  /** Whether the composer chain is active (false ⇒ caller renders direct). */
  readonly enabled: boolean;
  /** Render a frame (composer when enabled). dt seconds for animated passes. */
  render(dt: number): void;
  /** Match the drawing buffer (CSS px + dpr). Call on every resize. */
  setSize(width: number, height: number, dpr: number): void;
  /** Rebuild the chain for a new tier (high adds bloom, low goes direct). */
  setTier(tier: QualityTier): void;
  /** Tune bloom live. */
  setBloom(opts: BloomOptions): void;
  dispose(): void;
}

/**
 * Create the post-processing controller. Holds a reference to the renderer,
 * scene and camera so a tier change can rebuild the chain without the
 * caller re-wiring anything.
 */
export function createPostFX(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
  opts: PostFXOptions,
): PostFX {
  let tier = opts.tier;
  let composer: EffectComposer | null = null;
  let bloomPass: UnrealBloomPass | null = null;
  let vignettePass: ShaderPass | null = null;
  let outputPass: OutputPass | null = null;
  let renderPass: RenderPass | null = null;
  let size = new THREE.Vector2(1, 1);
  let dprCache = 1;

  const bloomCfg: Required<BloomOptions> = {
    strength: opts.bloom?.strength ?? 0.16,
    radius: opts.bloom?.radius ?? 0.26,
    threshold: opts.bloom?.threshold ?? 1.05,
  };

  const teardown = (): void => {
    bloomPass?.dispose();
    outputPass?.dispose();
    // ShaderPass / RenderPass own a fullscreen quad material; dispose what we can.
    vignettePass?.material.dispose();
    composer?.dispose();
    composer = null;
    bloomPass = null;
    vignettePass = null;
    outputPass = null;
    renderPass = null;
  };

  const build = (): void => {
    teardown();
    if (tier !== 'high') return; // low tier renders straight to the canvas
    composer = new EffectComposer(renderer);
    composer.setPixelRatio(dprCache);
    composer.setSize(size.x, size.y);

    renderPass = new RenderPass(scene, camera);
    composer.addPass(renderPass);

    bloomPass = new UnrealBloomPass(size.clone(), bloomCfg.strength, bloomCfg.radius, bloomCfg.threshold);
    composer.addPass(bloomPass);

    vignettePass = new ShaderPass(VignetteShader);
    const u = vignettePass.uniforms;
    // Fallbacks MUST match the shader's own defaults (0.18 / 1.05 / 0.45 —
    // deliberately pulled down after the 2026-06-29 owner playtest). They
    // were stale at 0.5 / 0.92 / 0.55, so any game passing a postfx object
    // for bloom silently got the old corner-crushing vignette back —
    // rendering tall-portrait frame corners pure black ("letterboxing").
    u.uStrength!.value = opts.vignette?.strength ?? 0.18;
    u.uRadius!.value = opts.vignette?.radius ?? 1.05;
    u.uSoftness!.value = opts.vignette?.softness ?? 0.45;
    if (opts.vignette?.maxDarken !== undefined) u.uMaxDarken!.value = opts.vignette.maxDarken;
    if (opts.vignette?.color !== undefined) u.uColor!.value = new THREE.Color(opts.vignette.color);
    composer.addPass(vignettePass);

    // OutputPass MUST be last: it applies the renderer's tone mapping +
    // sRGB, so bloom upstream operates in linear HDR (no double tone map).
    outputPass = new OutputPass();
    composer.addPass(outputPass);
  };

  build();

  return {
    get composer() {
      return composer;
    },
    get enabled() {
      return composer !== null;
    },
    render(dt) {
      if (composer) composer.render(dt);
      else renderer.render(scene, camera);
    },
    setSize(width, height, dpr) {
      size = new THREE.Vector2(Math.max(1, width), Math.max(1, height));
      dprCache = dpr;
      if (composer) {
        composer.setPixelRatio(dpr);
        composer.setSize(size.x, size.y);
        bloomPass?.setSize(size.x, size.y);
      }
    },
    setTier(next) {
      if (next === tier) return;
      tier = next;
      build();
    },
    setBloom(o) {
      if (o.strength !== undefined) bloomCfg.strength = o.strength;
      if (o.radius !== undefined) bloomCfg.radius = o.radius;
      if (o.threshold !== undefined) bloomCfg.threshold = o.threshold;
      if (bloomPass) {
        bloomPass.strength = bloomCfg.strength;
        bloomPass.radius = bloomCfg.radius;
        bloomPass.threshold = bloomCfg.threshold;
      }
    },
    dispose() {
      teardown();
    },
  };
}
